import { Link } from "react-router-dom";
import { ArrowLeft } from "lucide-react";

// StratIA Logo
const StratIALogo = ({ size = 32 }) => (
  <img 
    src="/stratia-logo.png" 
    alt="StratIA" 
    className="rounded-lg"
    style={{ width: size, height: size }}
  />
);

const TermsPage = () => {
  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="border-b border-border">
        <div className="max-w-4xl mx-auto px-6 py-4">
          <Link to="/" className="flex items-center gap-3">
            <StratIALogo size={32} />
            <span className="font-bold text-xl gradient-neural-text">StratIA</span>
          </Link>
        </div>
      </header>

      {/* Content */}
      <main className="max-w-4xl mx-auto px-6 py-12">
        <Link to="/" className="inline-flex items-center gap-2 text-primary hover:underline mb-8">
          <ArrowLeft className="w-4 h-4" />
          Volver al inicio
        </Link>

        <h1 className="text-4xl font-bold mb-8">Términos de Servicio</h1>
        
        <div className="prose prose-invert max-w-none space-y-6 text-muted-foreground">
          <p className="text-lg">
            Última actualización: Febrero 2025
          </p>

          <section className="bg-card/50 p-6 rounded-lg border border-border">
            <p className="text-foreground">
              Estos Términos de Servicio ("Términos") constituyen un acuerdo legal entre usted y <strong>StratIA</strong> ("la Compañía", "nosotros", "nuestro") 
              que rige el uso de la plataforma de gestión de redes sociales StratIA (el "Servicio").
            </p>
            <p className="mt-4">
              <strong>Nombre de la Compañía:</strong> StratIA<br />
              <strong>Sitio web:</strong> stratia.vip<br />
              <strong>Contacto:</strong> legal@stratia.vip
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">1. Aceptación de los Términos</h2>
            <p>
              Al acceder y utilizar StratIA ("el Servicio"), usted acepta estar sujeto a estos Términos de Servicio. 
              Si no está de acuerdo con alguna parte de estos términos, no podrá acceder al Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">2. Descripción del Servicio</h2>
            <p>
              StratIA es una plataforma de gestión de redes sociales que permite a los usuarios:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Programar y publicar contenido en múltiples plataformas sociales</li>
              <li>Gestionar cuentas de redes sociales conectadas</li>
              <li>Analizar el rendimiento del contenido</li>
              <li>Utilizar herramientas de IA para optimizar contenido</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">3. Cuentas de Usuario</h2>
            <p>
              Para utilizar ciertas funciones del Servicio, debe crear una cuenta. Usted es responsable de:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Mantener la confidencialidad de su contraseña</li>
              <li>Todas las actividades que ocurran bajo su cuenta</li>
              <li>Notificarnos inmediatamente sobre cualquier uso no autorizado</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">4. Conexión con Redes Sociales</h2>
            <p>
              Al conectar sus cuentas de redes sociales (Instagram, Facebook, Twitter, TikTok, YouTube, LinkedIn, Pinterest) 
              a StratIA, usted nos autoriza a:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Acceder a la información de su perfil público</li>
              <li>Publicar contenido en su nombre según sus instrucciones</li>
              <li>Leer métricas y estadísticas de sus publicaciones</li>
            </ul>
            <p className="mt-4">
              Puede revocar estos permisos en cualquier momento desconectando su cuenta desde la configuración de StratIA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">5. Uso Aceptable</h2>
            <p>
              Usted se compromete a no utilizar el Servicio para:
            </p>
            <ul className="list-disc pl-6 space-y-2 mt-4">
              <li>Publicar contenido ilegal, ofensivo o que infrinja derechos de terceros</li>
              <li>Spam o actividades de marketing no solicitado</li>
              <li>Violar los términos de servicio de las plataformas sociales conectadas</li>
              <li>Intentar acceder a cuentas o sistemas sin autorización</li>
            </ul>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">6. Propiedad Intelectual</h2>
            <p>
              El Servicio y su contenido original, características y funcionalidad son propiedad de StratIA 
              y están protegidos por leyes internacionales de derechos de autor.
            </p>
            <p className="mt-4">
              Usted conserva todos los derechos sobre el contenido que crea y publica a través de StratIA.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">7. Limitación de Responsabilidad</h2>
            <p>
              StratIA no será responsable de ningún daño indirecto, incidental, especial o consecuente 
              que resulte del uso o la imposibilidad de uso del Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">8. Modificaciones</h2>
            <p>
              Nos reservamos el derecho de modificar estos términos en cualquier momento. 
              Los cambios entrarán en vigor inmediatamente después de su publicación en el Servicio.
            </p>
          </section>

          <section>
            <h2 className="text-2xl font-semibold text-foreground mt-8 mb-4">9. Contacto</h2>
            <p>
              Si tiene preguntas sobre estos Términos de Servicio, puede contactarnos en:
            </p>
            <p className="mt-2 text-primary">
              contacto@stratia.vip
            </p>
          </section>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-border mt-16">
        <div className="max-w-4xl mx-auto px-6 py-8 text-center text-muted-foreground">
          <p>&copy; 2025 StratIA. Todos los derechos reservados.</p>
        </div>
      </footer>
    </div>
  );
};

export default TermsPage;
